<template>
  <div class="UIPageEmptyState">
    <UIEmptyState
      class="UIPageEmptyState__content"
      :icon="icon"
      size="lg"
      titleTag="h1"
      :title="title"
      :description="description"
    >
      <template v-if="hint" #description>
        {{ description }}
        <span class="UIPageEmptyState__hint">{{ hint }}</span>
      </template>

      <!-- Optional call to action — plenty of these states are terminal
           (an unpublished page, a maintenance window nobody can shortcut). -->
      <template v-if="$slots.actions" #actions>
        <slot name="actions" />
      </template>
    </UIEmptyState>
  </div>
</template>

<script lang="ts" setup>
import { UIEmptyState } from '@foundever/ui-lib'

import type { IIcons } from '@foundever/ui-lib'

/**
 * A whole-viewport empty state: token-based accent backdrop with a centred `UIEmptyState`.
 *
 * The shell for any page that is nothing but a message — a dead share link, a GPT that
 * was removed, a maintenance window. Carries no copy of its own; every caller states
 * what it is telling the visitor.
 */
defineProps<{
  icon?: IIcons
  title?: string
  description?: string
  /** Secondary line under the description — usually what the visitor can do next. */
  hint?: string
}>()
</script>

<style scoped lang="scss">
.UIPageEmptyState {
  box-sizing: border-box;
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(---fds-global-space-xl);
  // Token-only backdrop: a soft accent glow fading from the top edge, so it follows
  // the active color scheme (replaces the old light-only artwork).
  background-color: var(--bg-primary);
  background-image: radial-gradient(
    ellipse 120% 70% at 50% 0%,
    var(--bg-status-accent) 0%,
    transparent 70%
  );

  .UIPageEmptyState__content {
    width: 100%;
    :deep(.UIEmptyState__content) {
      max-width: 100%;
    }
  }

  .UIPageEmptyState__hint {
    display: block;
    margin-top: var(---fds-global-space-md);
    color: var(--text-tertiary);
  }
}
</style>
